import React, { memo, useCallback, useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import {
    Box, Button, Checkbox,
    DialogContent, Grid,
    InputAdornment, MenuItem, Tab, Tabs, TextField
} from '@mui/material';
import { VariableSizeList, ListChildComponentProps, areEqual } from 'react-window';
import CurrencyFormatField from 'components/common/CurrencyFormatField';
import { a11yProps, TabPanel } from 'components/mui/Tabs';
import Currency from 'components/formaters/Currency';
import { ModalActionProps } from 'models/AutoModals';
import { CarModel, FuelType } from 'models/StockModel';
import CopyableChassis from 'modules/cars/common/CopyableChassis';
import { exportCertificateRequired } from 'modules/cars/utils/common';
import spinner from 'recoil/atoms/spinner';
import { modifyArray, replaceItemAtIndex, setByPath } from 'utils/common';
import Api from 'utils/api';
import { useSnackbar } from "notistack";


type ToyamaPackagesDialogProps = {
    voyageId: number,
    toyama: boolean,
    isReady: (car: CarModel) => boolean,
} & ModalActionProps

type CarModelReady = CarModel & {
    ready: boolean,
}

const JpInvoicePackagesDialog = ({voyageId, onFinish, toyama, isReady}: ToyamaPackagesDialogProps) => {
    const {t} = useTranslation();
    const [tab, setTab] = useState('0');
    const [newCars, setNewCars] = useState<Record<number, CarModelReady[]>>({});
    const [newCarsNotReady, setNewCarsNotReady] = useState<Record<number, CarModelReady[]>>({});
    const [packages, setPackages] = useState<Record<number, Record<number, CarModelReady[]>>>({});

    useEffect(() => {
        Api.cars.voyage(voyageId, true).then(res => {
            const newCars: Record<number, CarModelReady[]> = {};
            const newCarsNotReady: Record<number, CarModelReady[]> = {};
            const packages: Record<number, Record<number, CarModelReady[]>> = {};

            res.data
                .sort((a: CarModel, b: CarModel) =>
                    ((a.jpInvoice?.package || 0) - (b.jpInvoice?.package || 0)) ||
                    ((a.jpInvoice?.batch || 0) - (b.jpInvoice?.batch || 0)) ||
                    ((a.jpInvoice?.pos || 0) - (b.jpInvoice?.pos || 0)) ||
                    a.id - b.id
                )
                .forEach((car: CarModel) => {
                    const pack = car.jpInvoice?.package || 0;
                    const company = car.company.id;

                    if (pack === 0) {
                        const ready = isReady(car);
                        if (ready) {
                            if (!(company in newCars))
                                newCars[company] = [];

                            newCars[company].push({...car, ready})
                        } else {
                            if (!(company in newCarsNotReady))
                                newCarsNotReady[company] = [];

                            newCarsNotReady[company].push({...car, ready})
                        }
                    } else {
                        if (!(pack in packages))
                            packages[pack] = {};

                        if (!(company in packages[pack]))
                            packages[pack][company] = [];

                        packages[pack][company].push({...car, ready: true})
                    }
                });

            setNewCars(newCars);
            setNewCarsNotReady(newCarsNotReady);
            setPackages(packages);
        })
        // @ts-ignore
    }, []);

    const updateReady = useCallback((company: number, idx: number, field: string, value: string) => setNewCars(old => {
        const car = setByPath(old[company][idx], field, value);

        return {
            ...old,
            [company]: replaceItemAtIndex(old[company], idx, car),
        }
    }), []);

    const updateNotReady = useCallback((company: number, idx: number, field: string, value: string) => setNewCarsNotReady(old => {
        const car = setByPath(old[company][idx], field, value);

        return {
            ...old,
            [company]: replaceItemAtIndex(old[company], idx, car),
        }
    }), []);

    const tabs = Object.keys(packages);

    if (Object.keys(newCars).length === 0 && Object.keys(newCarsNotReady).length === 0 && tabs.length === 0)
        return null;

    const id = 'toyama-packages';

    return (
        <>
            <DialogContent>
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    aria-label="JP invoice package tabs"
                >
                    {['0', ...tabs].map(num => (
                        <Tab
                            key={num}
                            value={num}
                            label={t('package_no', {number: num === '0' ? t('unprocessed') : num})}
                            {...a11yProps(id, num)}
                        />
                    ))}
                </Tabs>

                <TabPanel
                    id={id}
                    index={tab}
                    open={tab === '0'}
                    keepMounted
                    sx={{pt:2}}
                >
                    <Package
                        voyageId={voyageId}
                        companyGroups={newCars}
                        companyGroupsNotReady={newCarsNotReady}
                        updateReady={updateReady}
                        updateNotReady={updateNotReady}
                        toyama={toyama}
                        onFinish={onFinish}
                    />
                </TabPanel>

                {Object.entries(packages).map(([pack, companyGroups]) => (
                    <TabPanel
                        key={pack}
                        id={id}
                        index={pack}
                        open={tab === pack}
                        keepMounted
                        sx={{pt:2}}
                    >
                        <PackageStatic
                            voyageId={voyageId}
                            pack={pack}
                            companyGroups={companyGroups}
                            toyama={toyama}
                        />
                    </TabPanel>
                ))}



                {/*toyama && tab === 0 && Object.keys(notReadyCars).length > 0 && (
                    <>
                        <Box
                            bgcolor="error.light"
                            color="white"
                            textAlign="center"
                            fontWeight="bold"
                            padding="8px 0 4px"
                        >
                            {t('status_not_ready')}
                        </Box>

                        <Package
                            pack={-1}
                            companyGroups={notReadyCars}
                            update={updateNotReady}
                            toyama={toyama}
                        />
                    </>
                )}

                {!toyama && tab === 0 && (
                    <Box
                        bgcolor="error.light"
                        color="white"
                        textAlign="center"
                        fontWeight="bold"
                        padding="8px 0 4px"
                    >
                        {t('status_not_ready')}: {Object.values(notReadyCars).reduce((acc, val) => acc + val.length, 0)}
                    </Box>
                )*/}
            </DialogContent>
        </>
    );
};

const companyStyle = {
    textAlign: 'center',
    bgcolor: 'grey.100',
    color: 'primary.main',
    padding: '8px 0 4px',
}

const fuelTypes = Object.entries(FuelType);


type PackageProps = {
    voyageId: number,
    companyGroups: Record<number, CarModelReady[]>,
    companyGroupsNotReady?: Record<number, CarModelReady[]>,
    toyama: boolean,
}

type UpdatableProps = {
    updateReady: (company: number, idx: number, field: string, value: string) => void,
    updateNotReady: (company: number, idx: number, field: string, value: string) => void,
}

type Finished = {
    onFinish: (value: any) => void
}

type WithPack = {
    pack: string,
}

const Package = ({voyageId, companyGroups, companyGroupsNotReady, updateReady, updateNotReady, toyama, onFinish}: PackageProps & UpdatableProps & Finished) => {
    const {t} = useTranslation();
    const setSpinner = useSetRecoilState(spinner);
    const {enqueueSnackbar} = useSnackbar();
    const [selected, setSelected] = useState<number[]>(() => Object.values(companyGroups).flatMap(cars => cars.map(car => car.id)));

    const selectCar = (id: number, checked: boolean) => setSelected(old => modifyArray(old, id, checked));

    const submit = async () => {
        const cars: Record<number, any> = {};

        let hasEmptyRequired = false;

        [...Object.values(companyGroups), ...Object.values(companyGroupsNotReady!)].forEach(company => {
            company.forEach(car => {
                if (car.ready && (!car.year || !car.netWeight || !car.engineSize || !car.fuel || !car.unitPrice))
                    hasEmptyRequired = true;

                cars[car.id] = {
                    referenceNum: car.exportCertificate?.referenceNum,
                    year: car.year,
                    netWeight: car.netWeight,
                    engineSize: car.engineSize,
                    fuel: car.fuel,
                    unitPrice: car.unitPrice
                }
            })
        });

        if (hasEmptyRequired) {
            enqueueSnackbar(t('Fill empty fields in ready cars'), {
                variant: 'error',
            });
            return;
        }

        setSpinner(true);
        if (toyama) {
            Api.voyages.makeToyamaInvoice(voyageId, {cars, selected})
                .then(() => setSpinner(false))
                .then(onFinish);

            return;
        }

        if (Object.keys(cars).length > 0)
            await Api.cars.batchUpdate(cars)

        Api.voyages.invoices(voyageId)
            .then(() => setSpinner(false))
            .then(onFinish);
    };

    const flat = flattenedData(companyGroups, companyGroupsNotReady!);

    return (
        <>
            <VariableSizeList
                height={window.innerHeight - 300}
                width="100%"
                itemCount={flat.length}
                itemSize={index => flat[index].height}
                overscanCount={5}
                itemData={{
                    items: flat,
                    updateReady,
                    updateNotReady,
                    toyama,
                    selectCar,
                    t,
                }}
            >
                {WindowRow}
            </VariableSizeList>

            <Box sx={{textAlign: 'right', pt:2}}>
                <Button onClick={submit}>{t('generate')}</Button>
            </Box>
        </>
    )
};

type FlatData = {
    type: 'label' | 'car' | 'not-ready',
    name?: string,
    companyIdx?: number,
    car?: CarModelReady,
    height: number,
}

const flattenedData = (ready: Record<number, CarModelReady[]>, notReady: Record<number, CarModelReady[]>) => {
    const notReadyLabel: FlatData = {type: 'not-ready', height: 36};

    const accReady = Object.entries(ready)
        .reduce((acc, [_, cars]) => {
            acc.push({type: 'label', name: cars[0].company.name, height: 60});

            cars.forEach((car, companyIdx) => {
                acc.push({
                    type: 'car',
                    companyIdx,
                    car,
                    height: 41,
                });
            });

            return acc;
        }, [] as FlatData[]);

    const accNotReady = Object.entries(notReady)
        .reduce((acc, [_, cars]) => {
            acc.push({type: 'label', name: cars[0].company.name, height: 60});

            cars.forEach((car, companyIdx) => {
                acc.push({
                    type: 'car',
                    companyIdx,
                    car,
                    height: 41,
                });
            });

            return acc;
        }, [] as FlatData[]);

    if (accNotReady.length > 0)
        return [...accReady, notReadyLabel, ...accNotReady];

    return accReady;
};

const WindowRow = memo(({ data, index, style }: ListChildComponentProps) => {
    const item = data.items[index];

    if (item.type === 'label') {
        return <LabelRow
            style={style}
            name={item.name!}
            toyama={data.toyama}
        />;
    } else if (item.type === 'car') {
        return <CarRow
            style={style}
            idx={item.companyIdx!}
            car={item.car!}
            update={item.car!.ready ? data.updateReady : data.updateNotReady}
            toyama={data.toyama}
            selectCar={data.selectCar}
        />;
    } else if (item.type === 'not-ready') {
        return <Box
            bgcolor="error.light"
            color="white"
            textAlign="center"
            fontWeight="bold"
            padding="8px 0 4px"
            style={style}
        >
            {data.t('status_not_ready')}
        </Box>
    } else {
        return null;
    }
}, areEqual);

const PackageStatic = memo(({voyageId, pack, companyGroups, toyama}: PackageProps & WithPack) => {
    const {t} = useTranslation();
    const setSpinner = useSetRecoilState(spinner);

    const getToyamaInvoice = () => {
        setSpinner(true);
        Api.voyages.getToyamaInvoice(voyageId, +pack)
            .then(() => setSpinner(false))
    };

    return (
        <>
            <Box sx={{height: '70vh', overflowY: 'auto'}}>
                {Object.values(companyGroups).map(group => (
                    <Box key={group[0].company.id}>
                        <LabelRow
                            toyama={toyama}
                            name={group[0].company.name}
                        />

                        {group.map((car, idx) => (
                            <CarRowStatic
                                key={idx}
                                car={car}
                                toyama={toyama}
                            />
                        ))}
                    </Box>
                ))}
            </Box>

            {toyama && (
                <Box sx={{textAlign: 'right', pt:2}}>
                    <Button onClick={getToyamaInvoice}>{t('download')}</Button>
                </Box>
            )}
        </>
    )
});


type RowProps = {
    style?: React.CSSProperties,
    toyama: boolean,
}

type CarRowStaticProps = RowProps & {
    car: CarModelReady,
}

type CarRowProps = CarRowStaticProps & {
    idx: number,
    selectCar: (id: number, checked: boolean) => void,
    update: (company: number, idx: number, field: string, value: string) => void,
}

const LabelRow = memo(({style, toyama, name}: RowProps & {name: string}) => {
    const {t} = useTranslation('cars');

    return (
        <div style={style}>
            <Box sx={companyStyle}>{name}</Box>

            <Grid container columnSpacing={2}>
                <Grid item xs={1}></Grid>
                <Grid item xs={2}>{t('chassis')}</Grid>
                <Grid item xs={2}>{t('model')}</Grid>

                {toyama && (
                    <Grid item xs={2}>{t('reference_number')}</Grid>
                )}

                <Grid item xs={1}>{t('year')}</Grid>
                <Grid item xs={1}>{t('net_weight')}</Grid>
                <Grid item xs={1}>{t('engine_size')}</Grid>
                <Grid item xs={1}>{t('fuel')}</Grid>
                <Grid item xs={1}>{t('price')}</Grid>

                {!toyama && (
                    <Grid item xs={2}>{t('inner_cargo')}</Grid>
                )}
            </Grid>
        </div>
    )
});

const CarRow = memo(({idx, car, update, toyama, style, selectCar}: CarRowProps ) => {
    const {t} = useTranslation('cars');

    const companyId = car.company.id;

    return (
        <Box style={style} sx={{'&:hover': {bgcolor: 'grey.100'}}}>
            <Grid container columnSpacing={2} alignItems="center">
                <Grid item xs={1}>
                    {toyama && car.ready && (
                        <Checkbox
                            defaultChecked
                            value={car.id}
                            onChange={e => selectCar(car.id, e.target.checked)}
                        />
                    )}
                </Grid>

                <Grid item xs={2}>
                    <Box display="flex">
                        <CopyableChassis car={car}/>
                    </Box>
                </Grid>

                <Grid item xs={2}>{car.model}</Grid>

                {toyama && (
                    <Grid item xs={2}>
                        <TextField
                            placeholder={t('reference_number')}
                            fullWidth
                            value={car.exportCertificate?.referenceNum || ''}
                            onChange={e => update(companyId, idx,'exportCertificate.referenceNum', e.target.value)}
                            required={car.ready && exportCertificateRequired(car)}
                        />
                    </Grid>
                )}

                <Grid item xs={1}>
                    <TextField
                        placeholder={t('year')}
                        fullWidth
                        inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                        value={car.year || ''}
                        onChange={e => update(companyId, idx, 'year', e.target.value)}
                        required={car.ready}
                    />
                </Grid>

                <Grid item xs={1}>
                    <TextField
                        placeholder={t('net_weight')}
                        fullWidth
                        inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                        value={car.netWeight || ''}
                        onChange={e => update(companyId, idx, 'netWeight', e.target.value)}
                        required={car.ready}
                    />
                </Grid>

                <Grid item xs={1}>
                    <TextField
                        placeholder={`${t('engine_size')}, ${car.fuel === FuelType.ElectricVehicle ? 'kW' : 'cc'}`}
                        fullWidth
                        inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                        value={car.engineSize || ''}
                        onChange={e => update(companyId, idx, 'engineSize', e.target.value)}
                        required={car.ready}
                    />
                </Grid>

                <Grid item xs={1}>
                    <TextField
                        placeholder={t('fuel')}
                        fullWidth
                        select
                        value={car.fuel || ''}
                        onChange={e => update(companyId, idx, 'fuel', e.target.value)}
                        required={car.ready}
                    >
                        <MenuItem value=""><em>{t('unknown')}</em></MenuItem>
                        {fuelTypes.map(([name, value]) => (
                            <MenuItem
                                key={value}
                                value={value}
                            >
                                {value} - {name.split(/(?=[A-Z])/).join(' ')}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>

                <Grid item xs={1}>
                    <TextField
                        placeholder={t('price')}
                        fullWidth
                        value={car.unitPrice || ''}
                        onChange={e => update(companyId, idx, 'unitPrice', e.target.value)}
                        required={car.ready}
                        InputProps={{
                            inputComponent: CurrencyFormatField as any,
                            startAdornment: <InputAdornment position='start'>¥</InputAdornment>,
                        }}
                    />
                </Grid>

                {!toyama && (
                    <Grid item xs={2}>
                        {car.innerCargo.map(cargo => `${cargo.name}×${cargo.quantity}`).join(', ')}
                    </Grid>
                )}
            </Grid>
        </Box>
    )
});

const CarRowStatic = memo(({car, toyama}: CarRowStaticProps) => {
    return (
        <Box sx={{'&:hover': {bgcolor: 'grey.100'}}}>
            <Grid container columnSpacing={2}>
                <Grid item xs={1}>{car.jpInvoice?.batch}.{car.jpInvoice?.pos}</Grid>

                <Grid item xs={2}>
                    <Box display="flex">
                        <CopyableChassis car={car}/>
                    </Box>
                </Grid>

                <Grid item xs={2}>{car.model}</Grid>

                {toyama && (
                    <Grid item xs={2}>{car.exportCertificate?.referenceNum}</Grid>
                )}

                <Grid item xs={1}>{car.year}</Grid>
                <Grid item xs={1}>{car.netWeight}</Grid>
                <Grid item xs={1}>{car.engineSize }</Grid>
                <Grid item xs={1}>{car.fuel}</Grid>

                <Grid item xs={1}>
                    <Currency value={car.unitPrice}/>
                </Grid>

                {!toyama && (
                    <Grid item xs={2}>
                        {car.innerCargo.map(cargo => `${cargo.name}×${cargo.quantity}`).join(', ')}
                    </Grid>
                )}
            </Grid>
        </Box>
    )
});

export default JpInvoicePackagesDialog;
